import { Marker, MarkerOptions, Popup, PopupOptions } from 'mapbox-gl'
import React from 'react'
import ReactDOM from 'react-dom'

import { GeoPoint } from '../../helpers/gps'
import { createHTMLElement, latLng } from './mapUtils'
import { useMapElement } from './useMapElement'

type TProps = Required<SomeChildren> & {
  position: GeoPoint
  tooltip?: string
  tooltipOptions?: PopupOptions
  className?: string
  initialOptions?: MarkerOptions
}

export const MapMarker: React.FC<TProps> = ({
  children,
  position,
  className,
  tooltip,
  tooltipOptions = {},
  initialOptions = {},
}) => {
  const optionRef = React.useRef(initialOptions)

  const latLngMemo = React.useMemo(
    () => latLng(position.latitude, position.longitude),
    [position.latitude, position.longitude],
  )

  const marker = useMapElement<Marker>({
    onCreate() {
      const element = createHTMLElement('div', className)

      const marker = new Marker({
        element,
        ...optionRef.current,
      })

      marker.setLngLat(latLngMemo)

      return marker
    },
  })

  React.useEffect(() => {
    marker?.setLngLat(latLngMemo)
  }, [marker, latLngMemo])

  const tooltipOptionsRef = React.useRef(tooltipOptions)
  React.useEffect(() => {
    tooltipOptionsRef.current = tooltipOptions
  })

  React.useEffect(() => {
    if (tooltip) {
      const tooltipPopup = new Popup({
        closeButton: false,
        ...tooltipOptionsRef.current,
      })

      tooltipPopup.setText(tooltip)
      marker?.setPopup(tooltipPopup)
    }

    return () => {
      marker?.setPopup(undefined)
    }
  }, [marker, tooltip])

  React.useEffect(() => {
    return () => {
      marker?.remove()
    }
  }, [marker])

  const markerElement = marker?.getElement() ?? null
  return markerElement && ReactDOM.createPortal(children, markerElement)
}
